import { useState, useEffect, useMemo, useRef } from 'react'
import { inferTypeFromCode, InferredTypeResult } from '@mobileHelpers'

export interface UseInferredTypeResult {
  type: string
  fields?: Array<{ name: string; type: string; description?: string }>
  isInferring: boolean
  error?: string
}

/**
 * Hook for inferring TypeScript types from JavaScript code with debouncing and caching
 *
 * @param code - The JavaScript code to analyze
 * @param variableTypes - Record of variable names and their TypeScript type strings
 * @param debounceMs - Debounce delay in milliseconds (default: 500)
 * @returns Inferred type result with loading state
 *
 */
export function useInferredType(
  code: string,
  variableTypes: Record<string, string> = {},
  debounceMs: number = 500
): UseInferredTypeResult {
  const [result, setResult] = useState<InferredTypeResult>({ type: 'unknown' })
  const [isInferring, setIsInferring] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const cacheRef = useRef<Map<string, InferredTypeResult>>(new Map())

  // Create cache key from code and variable types
  const cacheKey = useMemo(() => {
    const varsKey = JSON.stringify(variableTypes)
    return `${code.trim()}|${varsKey}`
  }, [code, variableTypes])

  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Check cache first
    const cached = cacheRef.current.get(cacheKey)
    if (cached) {
      setResult(cached)
      setIsInferring(false)
      return
    }

    // If code is empty, return early
    if (!code || !code.trim()) {
      setResult({ type: 'unknown' })
      setIsInferring(false)
      return
    }

    setIsInferring(true)

    timeoutRef.current = setTimeout(() => {
      try {
        const inferred = inferTypeFromCode(code, variableTypes)

        // Cache the result
        cacheRef.current.set(cacheKey, inferred)

        // Limit cache size to prevent memory leaks
        if (cacheRef.current.size > 100) {
          const firstKey = cacheRef.current.keys().next().value
          cacheRef.current.delete(firstKey)
        }

        setResult(inferred)
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        setResult({ type: 'unknown', error: message })
      } finally {
        setIsInferring(false)
      }
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [code, variableTypes, cacheKey])

  return {
    type: result.type,
    fields: result.fields,
    isInferring,
    error: result.error,
  }
}
